/**
 * FikoMassInfo Dialog Component
 * Hacim, Yüzey Alanı, Kütle Merkezi ve Ağırlık Hesaplama UI
 * Body seçimi ile çalışır (CATIA tarzı)
 *
 * @author FikoYT
 * @version 1.2.0
 */

import { Component, Input, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, Inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoMassInfo, MassInfoSonuc, MALZEMELER, Malzeme } from './FikoMassInfo';
import * as THREE from 'three';

@Component({
  selector: 'fikoyt-mass-info',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="mi-panel">
      <!-- Body Seçimi -->
      <div class="mi-body-picker">
        <div class="mi-row body-row">
          <label>Body</label>
          <div class="body-input-group">
            <input type="text" class="body-input"
                   [value]="seciliBodyIsim" readonly
                   [class.selecting]="bodySecModu"
                   [class.has-body]="seciliBody"
                   placeholder="Tüm sahne">
            <button class="pick-btn" [class.active]="bodySecModu"
                    (click)="bodySecBaslat()" title="Body Seç">
              <i class="crosshairs icon"></i>
            </button>
            @if (seciliBody) {
              <button class="clear-btn" (click)="bodyTemizle()" title="Temizle">
                <i class="times icon"></i>
              </button>
            }
          </div>
        </div>
      </div>

      <!-- Malzeme Seçimi -->
      <div class="mi-row">
        <label>Malzeme</label>
        <select [(ngModel)]="seciliMalzemeIndex" (ngModelChange)="malzemeDegisti()">
          @for (m of malzemeler; track $index) {
            <option [ngValue]="$index">{{ m.ad }}</option>
          }
        </select>
      </div>

      <!-- Özel Yoğunluk (Özel seçilirse) -->
      @if (malzemeler[seciliMalzemeIndex]?.ad === 'Özel') {
        <div class="mi-row">
          <label>Yoğunluk (kg/dm³)</label>
          <input type="number" [(ngModel)]="ozelYogunluk" (ngModelChange)="malzemeDegisti()" step="0.01" min="0.01">
        </div>
      }

      <!-- Hesapla Butonu -->
      <div class="mi-buttons">
        <button class="mi-btn primary" (click)="hesapla()" [disabled]="isProcessing">
          {{ isProcessing ? '...' : 'Hesapla' }}
        </button>
        @if (sonuc) {
          <button class="mi-btn" (click)="markerToggle()" [class.active]="markerGorunur" title="Kütle Merkezi Göster">
            {{ markerGorunur ? '👁️' : '👁️‍🗨️' }}
          </button>
        }
      </div>

      <!-- Sonuçlar -->
      @if (sonuc) {
        <div class="mi-results">
          <!-- Kaynak -->
          <div class="mi-result-item small source-info">
            <span class="mi-label">Kaynak</span>
            <span class="mi-value">{{ sonucKaynak }}</span>
          </div>

          <!-- Hacim -->
          <div class="mi-result-item">
            <span class="mi-label">Hacim</span>
            <span class="mi-value">{{ formatNumber(sonuc.hacim.cm3) }} cm³</span>
          </div>

          <!-- Yüzey Alanı -->
          <div class="mi-result-item">
            <span class="mi-label">Yüzey Alanı</span>
            <span class="mi-value">{{ formatNumber(sonuc.yuzeyAlani.cm2) }} cm²</span>
          </div>

          <!-- Ağırlık -->
          <div class="mi-result-item highlight">
            <span class="mi-label">Ağırlık</span>
            <span class="mi-value">
              @if (sonuc.agirlik.kg >= 1) {
                {{ formatNumber(sonuc.agirlik.kg) }} kg
              } @else {
                {{ formatNumber(sonuc.agirlik.gram) }} g
              }
            </span>
          </div>

          <!-- Kütle Merkezi -->
          <div class="mi-result-item small">
            <span class="mi-label">Kütle Merkezi</span>
            <span class="mi-value coords">
              X: {{ formatNumber(sonuc.kutleMerkezi.x, 1) }}<br>
              Y: {{ formatNumber(sonuc.kutleMerkezi.y, 1) }}<br>
              Z: {{ formatNumber(sonuc.kutleMerkezi.z, 1) }}
            </span>
          </div>

          <!-- Boyutlar -->
          <div class="mi-result-item small">
            <span class="mi-label">Boyutlar (mm)</span>
            <span class="mi-value coords">
              {{ formatNumber(sonuc.boundingBox.boyutlar.x, 1) }} x
              {{ formatNumber(sonuc.boundingBox.boyutlar.y, 1) }} x
              {{ formatNumber(sonuc.boundingBox.boyutlar.z, 1) }}
            </span>
          </div>
        </div>
      }

      <!-- Hata Mesajı -->
      @if (hataMesaji) {
        <div class="mi-error">{{ hataMesaji }}</div>
      }
    </div>
  `,
  styles: [`
    .mi-panel {
      padding: 8px;
      min-width: 200px;
      max-width: 240px;
      font-size: 11px;
      background: linear-gradient(180deg, rgba(0,40,60,0.95) 0%, rgba(0,20,40,0.98) 100%);
      border-radius: 4px;
    }

    .mi-body-picker {
      margin-bottom: 8px;
    }

    .mi-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      padding: 4px 6px;
      background: rgba(0,0,0,0.3);
      border-radius: 3px;
      border: 1px solid rgba(0,200,220,0.2);
    }

    .mi-row.body-row {
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
    }

    .mi-row.body-row label {
      margin-bottom: 2px;
    }

    .mi-row label {
      color: rgba(0,220,240,0.9);
      font-size: 10px;
      font-weight: 500;
    }

    .body-input-group {
      display: flex;
      gap: 3px;
      align-items: center;
    }

    .body-input {
      flex: 1;
      background: rgba(0,40,60,0.8);
      border: 1px solid rgba(0,200,220,0.3);
      color: rgba(200,200,200,0.8);
      padding: 4px 6px;
      border-radius: 3px;
      font-size: 10px;
      outline: none;
      min-width: 0;
    }

    .body-input.selecting {
      border-color: rgba(255,200,0,0.8);
      background: rgba(60,40,0,0.6);
      animation: pulse 1s infinite;
    }

    .body-input.has-body {
      color: rgba(100,255,200,0.95);
      border-color: rgba(0,200,150,0.5);
    }

    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255,200,0,0.4); }
      50% { box-shadow: 0 0 8px 2px rgba(255,200,0,0.6); }
    }

    .pick-btn, .clear-btn {
      width: 24px;
      height: 24px;
      padding: 0;
      border: 1px solid rgba(0,200,220,0.4);
      background: rgba(0,60,80,0.6);
      color: rgba(0,220,240,0.9);
      border-radius: 3px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .pick-btn:hover {
      background: rgba(0,100,120,0.8);
      border-color: rgba(0,220,240,0.8);
    }

    .pick-btn.active {
      background: rgba(180,120,0,0.8);
      border-color: rgba(255,200,0,0.9);
      color: #fff;
    }

    .clear-btn {
      background: rgba(80,40,40,0.6);
      border-color: rgba(200,100,100,0.4);
      color: rgba(255,150,150,0.9);
    }

    .clear-btn:hover {
      background: rgba(120,50,50,0.8);
      border-color: rgba(255,100,100,0.8);
    }

    .pick-btn i, .clear-btn i {
      margin: 0 !important;
      font-size: 10px;
    }

    .mi-row select,
    .mi-row input[type="number"] {
      background: rgba(0,60,80,0.8);
      border: 1px solid rgba(0,200,220,0.4);
      color: #fff;
      padding: 2px 4px;
      border-radius: 3px;
      font-size: 10px;
      outline: none;
      cursor: pointer;
      max-width: 90px;
    }

    .mi-row select:focus,
    .mi-row input:focus {
      border-color: rgba(0,220,240,0.8);
    }

    .mi-buttons {
      display: flex;
      gap: 4px;
      margin-bottom: 8px;
    }

    .mi-btn {
      flex: 1;
      padding: 6px 10px;
      border: 1px solid rgba(0,200,220,0.5);
      background: rgba(0,60,80,0.6);
      color: #fff;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .mi-btn:hover {
      background: rgba(0,100,120,0.8);
      border-color: rgba(0,220,240,0.8);
    }

    .mi-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .mi-btn.primary {
      background: rgba(0,150,180,0.7);
      flex: 2;
    }

    .mi-btn.primary:hover {
      background: rgba(0,180,200,0.9);
    }

    .mi-btn.active {
      background: rgba(0,200,100,0.6);
      border-color: rgba(0,255,150,0.8);
    }

    .mi-results {
      background: rgba(0,0,0,0.4);
      border: 1px solid rgba(0,200,220,0.3);
      border-radius: 4px;
      padding: 6px;
    }

    .mi-result-item {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 4px 0;
      border-bottom: 1px solid rgba(0,200,220,0.15);
    }

    .mi-result-item:last-child {
      border-bottom: none;
    }

    .mi-result-item.highlight {
      background: rgba(0,200,100,0.15);
      margin: 2px -6px;
      padding: 6px;
      border-radius: 3px;
      border-bottom: none;
    }

    .mi-result-item.small {
      font-size: 9px;
    }

    .mi-result-item.source-info {
      background: rgba(0,100,150,0.15);
      margin: -6px -6px 4px -6px;
      padding: 4px 6px;
      border-radius: 3px 3px 0 0;
      border-bottom: 1px solid rgba(0,200,220,0.2);
    }

    .mi-label {
      color: rgba(0,220,240,0.8);
      font-size: 10px;
    }

    .mi-value {
      color: #fff;
      font-weight: 600;
      text-align: right;
    }

    .mi-value.coords {
      font-size: 9px;
      font-weight: 400;
      line-height: 1.4;
      font-family: monospace;
    }

    .mi-error {
      margin-top: 8px;
      padding: 6px;
      background: rgba(255,50,50,0.2);
      border: 1px solid rgba(255,100,100,0.4);
      border-radius: 3px;
      color: #ff8080;
      font-size: 10px;
      text-align: center;
    }
  `]
})
export class FikoMassInfoComponent implements OnDestroy {

  // ========== DIALOG PATTERN INPUT'LARI ==========

  private _anaGosterim: any = null;

  @Input()
  public get anaGosterim(): any {
    return this._anaGosterim;
  }
  public set anaGosterim(v: any) {
    this._anaGosterim = v;
  }

  private _pencere: any;
  private dialogCloseSub: Subscription | null = null;

  @Input()
  public get pencere(): any {
    return this._pencere;
  }
  public set pencere(v: any) {
    this._pencere = v?.instance || v;

    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }

    if (this._pencere && this._pencere.onDialogClose) {
      this.dialogCloseSub = this._pencere.onDialogClose.subscribe(() => {
        setTimeout(() => this.temizle(), 0);
      });
    }

    this.initComponent();
  }

  @Input() ayarlar: any = {};
  @Input() parametreler: any = {};

  // ========== BODY SEÇİMİ ==========

  seciliBody: any = null;
  bodySecModu: boolean = false;
  private bodySecimSubscription?: Subscription;

  get seciliBodyIsim(): string {
    if (!this.seciliBody) return '';
    return this.seciliBody.name || this.seciliBody.isim || 'Body';
  }

  // ========== COMPONENT STATE ==========

  malzemeler = MALZEMELER;
  seciliMalzemeIndex = 0; // Alüminyum varsayılan
  ozelYogunluk = 1.0;

  sonuc: MassInfoSonuc | null = null;
  sonucKaynak: string = '';
  hataMesaji: string | null = null;
  isProcessing = false;
  markerGorunur = false;

  private massInfoService: FikoMassInfo | null = null;
  private marker: THREE.Group | null = null;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.bodySecDurdur();
    this.temizle();
  }

  private initComponent(): void {
    if (this.parametreler?.anaGosterim) {
      this._anaGosterim = this.parametreler.anaGosterim;
    }
    if (!this.massInfoService) {
      this.massInfoService = new FikoMassInfo();
    }
  }

  private temizle(): void {
    // Marker'ı sahneden kaldır
    if (this.marker && this._anaGosterim?.sahneler?.[0]) {
      this._anaGosterim.sahneler[0].remove(this.marker);
      this.marker = null;
    }
    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }
    this.bodySecDurdur();
  }

  // ========== BODY SEÇME MODU ==========

  /**
   * Body seçme modunu başlat
   */
  bodySecBaslat(): void {
    // Zaten seçim modundaysa kapat
    if (this.bodySecModu) {
      this.bodySecDurdur();
      return;
    }

    this.bodySecModu = true;

    // FikoYT.degisti event'ini dinle - ürün ağacından seçim yapıldığında tetiklenir
    const fikoYT = this._anaGosterim?.FikoYT;
    if (fikoYT?.degisti) {
      this.bodySecimSubscription = fikoYT.degisti.subscribe(() => {
        if (this.bodySecModu) {
          // aktifItem seçilen öğeyi içerir
          const aktifItem = this._anaGosterim?.aktifItem;
          if (aktifItem) {
            this.seciliBody = aktifItem;
            this.bodySecDurdur();
            // Otomatik hesapla
            this.hesapla();
          }
        }
      });
    }

    this.cd.detectChanges();
  }

  /**
   * Body seçme modunu durdur
   */
  bodySecDurdur(): void {
    this.bodySecModu = false;

    if (this.bodySecimSubscription) {
      this.bodySecimSubscription.unsubscribe();
      this.bodySecimSubscription = undefined;
    }

    this.cd.detectChanges();
  }

  /**
   * Seçili body'yi temizle
   */
  bodyTemizle(): void {
    this.seciliBody = null;
    this.sonuc = null;
    this.sonucKaynak = '';
    this.cd.detectChanges();
  }

  // ========== MALZEME ==========

  malzemeDegisti(): void {
    if (this.massInfoService) {
      const malzeme = this.malzemeler[this.seciliMalzemeIndex];
      if (malzeme.ad === 'Özel') {
        this.massInfoService.yogunlukAyarla(this.ozelYogunluk);
      } else {
        this.massInfoService.malzemeAyarla(malzeme);
      }
      // Sonuç varsa yeniden hesapla
      if (this.sonuc) {
        this.hesapla();
      }
    }
  }

  // ========== HESAPLAMA ==========

  hesapla(): void {
    if (!this._anaGosterim?.sahneler?.[0]) {
      this.hataMesaji = 'Sahne bulunamadı';
      this.cd.detectChanges();
      return;
    }

    if (!this.massInfoService) {
      this.massInfoService = new FikoMassInfo();
    }

    this.isProcessing = true;
    this.hataMesaji = null;
    this.cd.detectChanges();

    try {
      // Malzeme ayarla
      const malzeme = this.malzemeler[this.seciliMalzemeIndex];
      if (malzeme.ad === 'Özel') {
        this.massInfoService.yogunlukAyarla(this.ozelYogunluk);
      } else {
        this.massInfoService.malzemeAyarla(malzeme);
      }

      const sahne = this._anaGosterim.sahneler[0];

      // Seçili body varsa onu hesapla, yoksa tüm sahneyi
      if (this.seciliBody) {
        this.sonuc = this.hesaplaBody(this.seciliBody);
        this.sonucKaynak = this.seciliBodyIsim;
      } else {
        this.sonuc = this.massInfoService.hesaplaToplam(sahne);
        this.sonucKaynak = 'Tüm sahne';
      }

      if (!this.sonuc) {
        this.hataMesaji = 'Mesh bulunamadı';
      } else if (this.sonuc.ucgenSayisi === 0) {
        this.hataMesaji = 'Geometri verisi yok';
        this.sonuc = null;
      }
    } catch (e: any) {
      this.hataMesaji = e.message || 'Hesaplama hatası';
      this.sonuc = null;
    } finally {
      this.isProcessing = false;
      this.cd.detectChanges();
    }
  }

  /**
   * Tek body için hesaplama
   * Sadece seçilen objenin KENDİ mesh'ini hesaplar (children dahil değil)
   */
  private hesaplaBody(body: any): MassInfoSonuc | null {
    if (!this.massInfoService || !body) return null;

    // Önce body'nin kendisine bak (mesh mi?)
    let hedefMesh: THREE.Mesh | null = null;

    // 1. Body'nin kendisi mesh mi?
    if (body instanceof THREE.Mesh && body.geometry) {
      hedefMesh = body;
    }
    // 2. Body'nin mesh property'si var mı? (bazı wrapper'lar için)
    else if (body.mesh instanceof THREE.Mesh && body.mesh.geometry) {
      hedefMesh = body.mesh;
    }
    // 3. Body'nin geometry'si var mı? (Group değil ama mesh gibi davranıyor)
    else if (body.geometry && body.geometry.getAttribute) {
      hedefMesh = body as THREE.Mesh;
    }
    // 4. FikoCizimYalin - ilk mesh'i al
    else if (body.cizimler?.children?.length > 0) {
      for (const child of body.cizimler.children) {
        if (child instanceof THREE.Mesh && child.geometry) {
          hedefMesh = child;
          break;
        }
      }
    }
    // 5. Group ise - SADECE direkt children mesh'leri al (traverse yok!)
    else if (body.children?.length > 0) {
      const meshler: THREE.Mesh[] = [];
      for (const child of body.children) {
        if (child instanceof THREE.Mesh && child.geometry && child.visible) {
          meshler.push(child);
        }
      }
      if (meshler.length > 0) {
        // Birden fazla mesh varsa hepsini hesapla
        const geciciGrup = new THREE.Group();
        for (const mesh of meshler) {
          geciciGrup.children.push(mesh);
        }
        const sonuc = this.massInfoService.hesaplaToplam(geciciGrup);
        geciciGrup.children.length = 0;
        return sonuc;
      }
    }

    // Tek mesh bulunduysa hesapla
    if (hedefMesh) {
      return this.massInfoService.hesaplaMesh(hedefMesh);
    }

    return null;
  }

  // ========== MARKER ==========

  markerToggle(): void {
    if (!this.sonuc || !this._anaGosterim?.sahneler?.[0]) return;

    const sahne = this._anaGosterim.sahneler[0];

    if (this.markerGorunur && this.marker) {
      // Marker'ı kaldır
      sahne.remove(this.marker);
      this.marker = null;
      this.markerGorunur = false;
    } else if (this.sonuc) {
      // Önceki marker varsa kaldır
      if (this.marker) {
        sahne.remove(this.marker);
      }

      // Marker oluştur ve ekle
      if (!this.massInfoService) {
        this.massInfoService = new FikoMassInfo();
      }

      const konum = new THREE.Vector3(
        this.sonuc.kutleMerkezi.x,
        this.sonuc.kutleMerkezi.y,
        this.sonuc.kutleMerkezi.z
      );

      // Boyut: BoundingBox'ın %3'ü
      const bbox = this.sonuc.boundingBox.boyutlar;
      const ortalamaBoyut = (bbox.x + bbox.y + bbox.z) / 3;
      const markerBoyut = Math.max(ortalamaBoyut * 0.03, 1);

      this.marker = this.massInfoService.kutleMerkeziMarkerOlustur(konum, markerBoyut);
      sahne.add(this.marker);
      this.markerGorunur = true;

      // Render tetikle
      if (this._anaGosterim.ciz) {
        this._anaGosterim.ciz();
      }
    }

    this.cd.detectChanges();
  }

  // ========== UTILS ==========

  formatNumber(value: number, decimals: number = 2): string {
    if (value === 0) return '0';
    if (Math.abs(value) < 0.01) return value.toExponential(1);
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
  }
}
